import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, ImageIcon, Sparkles, PenTool, UserRound, ScanText, ArrowRight, Loader2, RefreshCw, Plus, Trash2, Wand2 } from "lucide-react";

const methodMeta = {
  ai: { label: "AI Recreated", color: "var(--c-sky)", icon: Sparkles },
  local: { label: "Local Overlay", color: "var(--c-teal)", icon: PenTool },
  "human-skip": { label: "Human Photo — Skipped", color: "var(--c-amber)", icon: UserRound },
  skip: { label: "No Change", color: "var(--tg-faint)", icon: ImageIcon },
};

function peopleClass(r) {
  if (r.removed_human) return "text-[var(--c-emerald)]";
  if (r.is_human) return "text-[var(--c-amber)]";
  return "text-[var(--tg-muted)]";
}

function peopleLabel(r) {
  if (r.removed_human) return "Removed";
  if (r.is_human) return "Detected";
  return "None";
}

export default function Sandbox() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFile, setLastFile] = useState(null);
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({ old_value: "", new_value: "" });
  const [aiStatus, setAiStatus] = useState(null);
  const fileRef = useRef();

  const loadRules = async () => {
    const { data } = await api.get("/rules");
    setRules(data);
  };
  useEffect(() => {
    loadRules();
    api.get("/ai/status").then(({ data }) => setAiStatus(data)).catch(() => setAiStatus(null));
  }, []);

  const runPreview = async (file) => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/sandbox/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success("Preview generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLastFile(f);
    await runPreview(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Rule editing (shared with Replacements page via same API)
  const updateRuleLocal = (id, field, value) =>
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const saveRule = async (r) => {
    await api.put(`/rules/${r.id}`, { old_value: r.old_value, new_value: r.new_value });
    toast.success("Rule saved");
  };
  const toggleRule = async (r) => {
    await api.put(`/rules/${r.id}`, { enabled: !r.enabled });
    loadRules();
  };
  const deleteRule = async (id) => {
    await api.delete(`/rules/${id}`);
    loadRules();
  };
  const addRule = async () => {
    if (!newRule.old_value) return toast.error("Enter the text to find");
    await api.post("/rules", { ...newRule, category: "brand", enabled: true });
    setNewRule({ old_value: "", new_value: "" });
    toast.success("Rule added");
    loadRules();
  };

  const mm = result ? methodMeta[result.method] || methodMeta.skip : null;
  const MI = mm?.icon;

  return (
    <div className="w-full max-w-[1800px] space-y-5">
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[rgb(var(--c-sky-rgb)/0.10)] border border-[rgb(var(--c-sky-rgb)/0.30)] grid place-items-center">
              <ScanText className="h-5 w-5 text-[var(--c-sky)]" />
            </div>
            <div>
              <div className="font-head font-bold text-lg">Preview Sandbox</div>
              <p className="text-sm text-[var(--tg-faint)]">
                Upload an image, tweak rules below, and re-run to see OCR text swap, watermark, and human-photo exclusion.
              </p>
              {aiStatus && (
                <p className={`text-xs mt-1 ${aiStatus.image === "available" ? "text-[var(--c-emerald)]" : "text-[var(--c-amber)]"}`}>
                  Gemini vision: {aiStatus.vision}. Image editing: {aiStatus.image === "rate_limited" ? "rate-limited" : aiStatus.image}.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onFile}
              data-testid="sandbox-file-input"
            />
            {lastFile && (
              <Button
                variant="outline"
                onClick={() => runPreview(lastFile)}
                disabled={loading}
                className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"
                data-testid="sandbox-rerun-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Re-run
              </Button>
            )}
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={loading}
              className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold"
              data-testid="sandbox-upload-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {loading ? "Processing..." : "Upload Image"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Inline rule editor */}
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-6" data-testid="sandbox-rules-editor">
        <div className="flex items-center gap-2 mb-4">
          <Wand2 className="h-4 w-4 text-[var(--c-teal)]" />
          <span className="font-head font-bold text-base">Replacement Rules</span>
          <span className="text-xs text-[var(--tg-faint)]">— edit and re-run instantly</span>
        </div>
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center gap-2 font-mono text-sm" data-testid={`sandbox-rule-${r.id}`}>
              <Input
                value={r.old_value}
                onChange={(e) => updateRuleLocal(r.id, "old_value", e.target.value)}
                onBlur={() => saveRule(r)}
                className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] h-9"
                data-testid={`sandbox-rule-old-${r.id}`}
              />
              <ArrowRight className="h-4 w-4 text-[var(--tg-faint)] shrink-0" />
              <Input
                value={r.new_value}
                onChange={(e) => updateRuleLocal(r.id, "new_value", e.target.value)}
                onBlur={() => saveRule(r)}
                className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] h-9"
                data-testid={`sandbox-rule-new-${r.id}`}
              />
              <Switch checked={r.enabled} onCheckedChange={() => toggleRule(r)} data-testid={`sandbox-rule-toggle-${r.id}`} />
              <button onClick={() => deleteRule(r.id)} className="text-[var(--tg-faint)] hover:text-[var(--c-rose)]" data-testid={`sandbox-rule-delete-${r.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--tg-border)]">
          <Input
            placeholder="find (e.g. VAJRA TRADER)"
            value={newRule.old_value}
            onChange={(e) => setNewRule({ ...newRule, old_value: e.target.value })}
            className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] h-9 font-mono"
            data-testid="sandbox-new-old"
          />
          <ArrowRight className="h-4 w-4 text-[var(--tg-faint)] shrink-0" />
          <Input
            placeholder="replace with"
            value={newRule.new_value}
            onChange={(e) => setNewRule({ ...newRule, new_value: e.target.value })}
            className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] h-9 font-mono"
            data-testid="sandbox-new-new"
          />
          <Button onClick={addRule} className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white h-9" data-testid="sandbox-add-rule">
            <Plus className="h-4 w-4" />
          </Button>
          {lastFile && (
            <Button variant="outline" onClick={() => runPreview(lastFile)} disabled={loading} className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)] h-9" data-testid="sandbox-rerun-inline">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>
      </Card>

      {loading && (
        <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-12 text-center" data-testid="sandbox-loading">
          <Loader2 className="h-8 w-8 mx-auto text-[var(--c-sky)] animate-spin" />
          <p className="text-[var(--tg-faint)] mt-3 text-sm">Running OCR / AI detection, replacement and watermark… this can take a few seconds.</p>
        </Card>
      )}

      {result && !loading && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4" data-testid="sandbox-original">
              <div className="label-caps mb-3">Original</div>
              <div className="rounded-md overflow-hidden bg-[var(--tg-bg)] grid place-items-center min-h-[220px]">
                <img src={result.original} alt="original" className="max-h-[420px] w-auto object-contain" />
              </div>
            </Card>
            <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4" data-testid="sandbox-processed">
              <div className="label-caps mb-3 text-[var(--c-sky)]">Processed Output</div>
              <div className="rounded-md overflow-hidden bg-[var(--tg-bg)] grid place-items-center min-h-[220px]">
                <img src={result.processed} alt="processed" className="max-h-[420px] w-auto object-contain" />
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5" data-testid="sandbox-status">
              <div className="label-caps mb-3">Result</div>
              <div
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold"
                style={{ color: mm.color, background: `${mm.color}1a` }}
                data-testid="sandbox-method"
              >
                {MI && <MI className="h-4 w-4" />}
                {mm.label}
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[var(--tg-faint)]">People</span>
                  <span className={peopleClass(result)}>
                    {peopleLabel(result)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--tg-faint)]">Watermark</span>
                  <span className={result.watermark_enabled ? "text-[var(--c-emerald)]" : "text-[var(--tg-faint)]"}>
                    {result.watermark_enabled ? "Applied" : "Off"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--tg-faint)]">In-image OCR</span>
                  <span className={result.image_text_enabled ? "text-[var(--c-emerald)]" : "text-[var(--tg-faint)]"}>
                    {result.image_text_enabled ? "Enabled" : "Off"}
                  </span>
                </div>
                {result.ai_unavailable && (
                  <p className="text-xs text-[var(--c-amber)] pt-1">AI image generation is temporarily rate-limited; original preserved.</p>
                )}
                {result.ai_rejected && (
                  <p className="text-xs text-[var(--c-amber)] pt-1">AI changed too much of the artwork, so CloneCast rejected it and preserved the original.</p>
                )}
              </div>
            </Card>

            <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5" data-testid="sandbox-ocr">
              <div className="label-caps mb-3">Detected Text (OCR)</div>
              {result.ocr_lines.length === 0 ? (
                <p className="text-sm text-[var(--tg-faint)]">No text detected by Tesseract{result.ai_detected ? " — AI vision read the stylized text instead." : "."}</p>
              ) : (
                <div className="space-y-1.5 font-mono text-xs max-h-40 overflow-y-auto">
                  {result.ocr_lines.map((t, i) => (
                    <div key={`${t}-${i}`} className="px-2 py-1 rounded bg-[var(--tg-elevated)] text-[var(--tg-muted)] truncate">{t}</div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5" data-testid="sandbox-matches">
              <div className="label-caps mb-3">Matched Rules</div>
              {result.ai_detected ? (
                <div className="flex items-center gap-2 text-sm text-[var(--c-sky)]">
                  <Sparkles className="h-4 w-4" />
                  AI detected & recreated the branded text (stylized logo).
                </div>
              ) : result.matched.length === 0 ? (
                <p className="text-sm text-[var(--tg-faint)]">No replacement rules matched the detected text.</p>
              ) : (
                <div className="space-y-2">
                  {result.matched.map((m, i) => (
                    <div key={`${m.old}-${m.new}-${i}`} className="flex items-center gap-2 font-mono text-xs" data-testid={`sandbox-match-${i}`}>
                      <span className="px-2 py-1 rounded bg-[rgb(var(--c-rose-rgb)/0.10)] text-[var(--c-rose)] truncate max-w-[40%]">{m.old}</span>
                      <ArrowRight className="h-3 w-3 text-[var(--tg-faint)] shrink-0" />
                      <span className="px-2 py-1 rounded bg-[rgb(var(--c-emerald-rgb)/0.10)] text-[var(--c-emerald)] truncate max-w-[40%]">{m.new || "(removed)"}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {!result && !loading && (
        <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-12 text-center" data-testid="sandbox-empty">
          <ImageIcon className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />
          <p className="text-[var(--tg-faint)] mt-3 text-sm">Upload a promotional graphic to test the branding pipeline.</p>
        </Card>
      )}
    </div>
  );
}
